/**
 * Service de vérification d'email
 * Utilise le backend pour l'envoi d'emails et la vérification des codes
 */

import { API_ENDPOINTS, fetchWithTimeout, parseJsonResponse } from '../config/api';

/**
 * Crée et envoie un code de vérification via le backend
 * @param {string} email - Email à vérifier
 * @returns {Promise<boolean>} - True si succès
 */
export const createAndSendVerificationCode = async (email) => {
  if (!email || !email.trim()) {
    throw new Error('Email requis');
  }

  const emailTrimmed = email.trim().toLowerCase();

  // Validation basique du format email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(emailTrimmed)) {
    throw new Error('Format d\'email invalide');
  }

  try {
    const response = await fetchWithTimeout(
      API_ENDPOINTS.SEND_VERIFICATION_CODE,
      {
        method: 'POST',
        body: JSON.stringify({
          email: emailTrimmed
        })
      },
      15000 // 15 secondes de timeout
    );

    const data = await parseJsonResponse(response);

    if (data.success) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Code de vérification envoyé à ${emailTrimmed}`);
      }
      return true;
    } else {
      throw new Error(data.message || 'Impossible d\'envoyer l\'email de vérification');
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Erreur lors de l\'envoi du code:', error);
    }
    
    // Propager l'erreur avec un message utilisateur-friendly
    if (error.message.includes('expiré') || error.message.includes('timeout')) {
      throw new Error('La requête a expiré. Veuillez réessayer.');
    }
    throw error;
  }
};

/**
 * Vérifie un code de vérification via le backend
 * @param {string} email - Email à vérifier
 * @param {string} code - Code à vérifier
 * @returns {Promise<boolean>} - True si le code est valide
 */
export const verifyCode = async (email, code) => {
  if (!email || !email.trim() || !code || code.length !== 6) {
    return false;
  }

  const emailTrimmed = email.trim().toLowerCase();

  // Validation que le code contient uniquement des chiffres
  if (!/^\d{6}$/.test(code)) {
    return false;
  }

  try {
    const response = await fetchWithTimeout(
      API_ENDPOINTS.VERIFY_CODE,
      {
        method: 'POST',
        body: JSON.stringify({
          email: emailTrimmed,
          code: code
        })
      },
      10000 // 10 secondes de timeout
    );

    const data = await parseJsonResponse(response);

    if (data.success) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Code vérifié avec succès pour ${emailTrimmed}`);
      }
      return true;
    } else {
      return false;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Erreur lors de la vérification:', error);
    }
    
    // Si c'est une erreur 429 (trop de tentatives), propager l'erreur
    if (error.message.includes('trop de tentatives') || error.message.includes('429')) {
      throw new Error('Trop de tentatives échouées. Veuillez demander un nouveau code.');
    }
    
    return false;
  }
};

/**
 * Envoie l'email de confirmation d'inscription via le backend
 * @param {string} email - Email de l'inscrit
 * @param {string} firstName - Prénom pour personnaliser le message
 * @returns {Promise<boolean>} - True si l'email a été envoyé
 */
export const sendInscriptionConfirmation = async (email, firstName) => {
  if (!email || !email.trim() || !firstName || !firstName.trim()) {
    return false;
  }

  try {
    const response = await fetchWithTimeout(
      API_ENDPOINTS.SEND_INSCRIPTION_CONFIRMATION,
      {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim()
        })
      },
      10000
    );

    const data = await parseJsonResponse(response);
    return data.success === true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Erreur envoi email confirmation:', error);
    }
    return false;
  }
};
